"use client";
import { setMandateStatusAction } from "@/actions/user.actions";
import { useUserContext } from "@/context/UserProvider";
import { useStatsigReady } from "@/hooks/useStatsigReady";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import {
  checkIsWebOS,
  handleBackButtonPress,
  isTizenBackButton,
  isTvEnv,
  isWebOsBackButton,
  shoudlPreventResponsivenessOnLowerLg,
  updateUserClientId,
} from "@/utils/common.helpers";
import { useRouter } from "next/navigation";
import { useEffect, useState, lazy, Suspense } from "react";
import { LazyToastContainer } from "./LazyToastContainer";
import { Spinner } from "./Spinner";

const StatsigExperimentManager = lazy(() =>
  import("./StatsigExperimentManager").then((mod) => ({
    default: mod.StatsigExperimentManager,
  })),
);

const ProfileLoader = lazy(() =>
  import("@/screens/profile/components/ProfileLoader").then((mod) => ({
    default: mod.ProfileLoader,
  })),
);

const USER_TRACKING_DELAY_MS = 100;
const MANDATE_STATUS_DELAY_MS = 500;

export const ClientInitializer = () => {
  const { identifyUser } = useTrackingAnalytics();
  const { user } = useUserContext();
  const router = useRouter();
  const isStatsigReady = useStatsigReady();
  const [isLoading, setIsLoading] = useState(isTvEnv());

  useEffect(() => {
    if (isTvEnv()) {
      import("@noriginmedia/norigin-spatial-navigation").then((spatialNav) => {
        spatialNav.init({
          useGetBoundingClientRect: true,
          distanceCalculationMethod: "center",
        });

        spatialNav.setKeyMap({
          left: [37, 21], // LEFT arrow and Samsung TV left
          up: [38, 19], // UP arrow and Samsung TV up
          right: [39, 22], // RIGHT arrow and Samsung TV right
          down: [40, 20], // DOWN arrow and Samsung TV down
          enter: [13, 23], // ENTER and Samsung TV OK
        });
      });
    }

    if (isTvEnv()) {
      if (!shoudlPreventResponsivenessOnLowerLg() && window.innerWidth > 1280) {
        document.documentElement.style.fontSize = "20px";
      }

      const isWebOS = checkIsWebOS();
      const keydownHandler = (e: KeyboardEvent) => {
        const isBackButton = isWebOS
          ? isWebOsBackButton(e)
          : isTizenBackButton(e);

        if (isBackButton) {
          handleBackButtonPress(router);
          e.preventDefault();
          e.stopPropagation();
        }
      };
      document.addEventListener("keydown", keydownHandler);

      setIsLoading(false);

      const trackingTimerId = setTimeout(() => {
        if (user.userId) {
          identifyUser(user);
        }
        updateUserClientId();
      }, USER_TRACKING_DELAY_MS);

      const mandateTimerId = setTimeout(() => {
        setMandateStatusAction();
      }, MANDATE_STATUS_DELAY_MS);

      return () => {
        document.removeEventListener("keydown", keydownHandler);
        clearTimeout(trackingTimerId);
        clearTimeout(mandateTimerId);
      };
    } else {
      setIsLoading(false);

      const trackingTimerId = setTimeout(() => {
        if (user.userId) {
          identifyUser(user);
        }
        updateUserClientId();
      }, USER_TRACKING_DELAY_MS);

      const mandateTimerId = setTimeout(() => {
        setMandateStatusAction();
      }, MANDATE_STATUS_DELAY_MS);

      return () => {
        clearTimeout(trackingTimerId);
        clearTimeout(mandateTimerId);
      };
    }
  }, [user, identifyUser, router]);

  return (
    <>
      <Suspense fallback={null}>
        {isStatsigReady && <StatsigExperimentManager />}
      </Suspense>
      <LazyToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        draggable
        theme="dark"
      />
      <Suspense fallback={null}>
        <ProfileLoader />
      </Suspense>
      {isLoading && (
        <div className="fixed inset-0 z-[51] flex items-center justify-center bg-stg-grey-0">
          <Spinner />
        </div>
      )}
    </>
  );
};
